import {useCallback, useRef, useState} from 'react';

import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Observer} from 'mobx-react';

import type {SendCodeResult, SendTo} from '@yourcoach/shared/modules/auth';

import {useI18n} from '@yourcoach/i18n';
import {alert} from '@yourcoach/shared/components/alert';
import {Modal} from '@yourcoach/shared/components/Modal';
import {ApplyCodeStore, SendCodeStore} from '@yourcoach/shared/modules/auth';
import {useMobxStore} from '@yourcoach/shared/utils/store';

import {AccountForm} from './AccountForm';
import {EnterCodeForm} from './EnterCodeForm';

type Props = {
  type?: 'email' | 'phone';
};

export const AddAccountsModal = NiceModal.create<Props>(({type}) => {
  const {t} = useI18n();
  const modal = useModal();

  const [sendCodeResult, setSendCodeResult] = useState<SendCodeResult>();
  const sendToRef = useRef<SendTo>({email: ''});

  const sendCodeStore = useMobxStore(
    () =>
      new SendCodeStore({
        captchaKey: process.env.CAPTCHA_KEY!,
        forAccount: true,
        isAuthFlow: false,
        isLoginFlow: false,
        mockCaptcha: !!MOCK_CAPTCHA,
      }),
  );

  const applyCodeStore = useMobxStore(
    () =>
      new ApplyCodeStore({
        captchaKey: process.env.CAPTCHA_KEY!,
        mockCaptcha: !!MOCK_CAPTCHA,
        onApplyCodeError: error => {
          if (error.code === 'error.resource.user.blocked_account') {
            setSendCodeResult(undefined);
          }

          alert.show({
            message: error.message,
            title: error.title,
          });
        },
        onApplyCodeSuccess() {
          modal.hide();
        },
        onSendCodeError: error => {
          alert.show({
            message: error.message,
            title: error.title,
          });
        },
      }),
  );

  const sendCode = useCallback(
    async (to: SendTo) => {
      try {
        // @ts-expect-error: TODO: fix this
        applyCodeStore.setSendCodeResult(undefined);

        const result = await sendCodeStore.sendCode(to);

        applyCodeStore.setSendCodeResult(result);
        applyCodeStore.setSentTo(to);

        setSendCodeResult(result);
      } catch (error) {
        alert.show({
          message: error.message,
          title: error.title,
        });
      }
    },
    [applyCodeStore, sendCodeStore],
  );

  const onAccountFormSubmit = useCallback(
    (to: SendTo) => {
      sendToRef.current = to;

      sendCode(to);
    },
    [sendCode],
  );

  const onEnterCodeFormSubmit = useCallback(
    (code: string) => {
      applyCodeStore.setCode(code);
    },
    [applyCodeStore],
  );

  const onResendCodeButtonClick = useCallback(() => {
    sendCode(sendToRef.current);
  }, [sendCode]);

  return (
    <Observer>
      {() => (
        <Modal
          isFullWidth
          isOpen={modal.visible}
          maxWidth={'450px'}
          onAfterClose={modal.remove}
          onRequestClose={modal.hide}
          title={
            sendCodeResult
              ? t('web-app:AddAccount.title.verification')
              : t('web-app:AddAccount.title.account')
          }
        >
          {sendCodeResult ? (
            <Observer>
              {() => (
                <EnterCodeForm
                  code={applyCodeStore.code || ''}
                  inProgress={
                    sendCodeStore.inProgress || applyCodeStore.inProgress
                  }
                  onCodeChange={onEnterCodeFormSubmit}
                  onResendCodeButtonClick={onResendCodeButtonClick}
                  resendTimeout={applyCodeStore.resendTimeout}
                  sentTo={applyCodeStore.sentTo}
                />
              )}
            </Observer>
          ) : (
            <Observer>
              {() => (
                <AccountForm
                  inProgress={sendCodeStore.inProgress}
                  onSubmit={onAccountFormSubmit}
                  type={type}
                />
              )}
            </Observer>
          )}
        </Modal>
      )}
    </Observer>
  );
});
